import { createClient } from "@supabase/supabase-js";
import { message, Spin } from "antd";
import React, { useEffect, useState } from "react";

import { getSupaClient } from "../../lib/supabase";
import DiscordMembersTable from "./DiscordMembersTable";
import UserTable from "./UserTable";

const CustomFilterTab = (props) => {
  // props.viewId - id of the user_views record this tab is rendering
  // props.startTime - start time for member analytics
  // props.endTime - end time for member analytics
  // props.onGuildTagUpdate - callback in parent to pass down to DiscordMembersTable
  // props.onMemberTagAdded - callback in parent to pass down to DiscordMembersTable
  // props.onMemberTagRemoved - callback in parent to pass down to DiscordMembersTable
  // props.onBulkTag - callback in parent to pass down to DiscordMembersTable

  const supabase = getSupaClient();
  const platform = localStorage.getItem("platform");
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState({});
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [filteredUsersActivity, setUserActivity] = useState([]);
  const [totalMembers, setTotalMembers] = useState(0);
  const [messagesSent, setMessagesSent] = useState(0);
  const [activeThisPeriod, setActiveThisPeriod] = useState(0);
  const [reactionAdded, setReactionAdded] = useState(0);

  const [filteredUsersActivityByRole, setFilteredByRole] = useState([]);

  const teamId =
    platform === "slack"
      ? localStorage.getItem("team-id")
      : localStorage.getItem("guild-id");

  /**
   * Update a member's tag data in the source data for a member table, if the given member is
   * present in the table data.
   * @param membersData source data for a member table
   * @param updateFn setState function for a member table
   * @param memberId member id to update
   * @param memberTag new tag data to update member row with
   */
  async function addMemberTag(membersData, updateFn, memberId, memberTag) {
    const memberIdx = membersData?.findIndex((m) => m.id === memberId);
    if (memberIdx !== undefined && memberIdx !== -1 && membersData) {
      const member = membersData[memberIdx];
      const newTag = {
        id: memberTag.id,
        name: memberTag.name,
        color: memberTag.color,
      };
      let newTags = member.tags;
      if (newTags) newTags.push(newTag);
      else newTags = [newTag];
      member.tags = newTags;
      const newMembersData = [...membersData];
      newMembersData[memberIdx] = member;
      updateFn(newMembersData);
    }
  }

  /**
   * Remove a tag from a member's tag data in the source data for a member table, if the given
   * member is present in the table data.
   * @param membersData source data for a member table
   * @param updateFn setState function for a member table
   * @param memberId member id to update
   * @param memberTag tag to remove from the member row
   */
  async function removeMemberTag(membersData, updateFn, memberId, memberTag) {
    // console.log("membersData:", membersData);
    // console.log("memberTag:", memberTag);
    const memberIdx = membersData?.findIndex((m) => m.id === memberId);
    // console.log("memberIdx:", memberIdx);
    if (memberIdx !== undefined && memberIdx !== -1 && membersData) {
      const member = membersData[memberIdx];
      let newTags = member.tags;
      if (newTags) {
        newTags = newTags.filter((tag) => tag.id !== memberTag.id);
        member.tags = newTags;
        const newMembersData = [...membersData];
        newMembersData[memberIdx] = member;
        updateFn(newMembersData);
      }
    }
  }

  /**
   * Propagate a new member tag across all source data tables on the Discord members tab,
   * including Custom tabs
   * @param memberId discord member id
   * @param memberTag new tag that should be propagated to all data tables
   */
  async function addMemberTagAllTabs(memberId, memberTag) {
    props.onMemberTagAdded(memberId, memberTag);
    addMemberTag(
      filteredUsersActivityByRole,
      setFilteredByRole,
      memberId,
      memberTag
    );
    props.loadViews();
  }

  /**
   * Propagate a removed member tag across all source data tables on the Discord members tab,
   * including Custom tabs
   * @param memberId discord member id
   * @param memberTag new tag that should be propagated to all data tables
   */
  async function removeMemberTagAllTabs(memberId, memberTag) {
    props.onMemberTagRemoved(memberId, memberTag);
    removeMemberTag(
      filteredUsersActivityByRole,
      setFilteredByRole,
      memberId,
      memberTag
    );
    props.loadViews();
  }

  const filterUsers = async (customView) => {
    let base;
    if (platform === "slack")
      base = supabase
        .from("slack_users")
        .select(
          `
            name,
            slack_id,
            tags (id, name),
            email,
            joined_formatted_ts,
            tz_label
          `
        )
        .filter("team", "eq", teamId)
        .neq("is_bot", true)
        .neq("is_deleted", true);
    else
      base = supabase
        .from("discord_members")
        .select(
          `
          username,
          id,
          discord_id,
          discord_roles!inner(discord_id, name),
          tags (id, name),
          joined_at`
        )
        .filter("guild", "eq", teamId);

    // only considering first filter currently
    const userFilter = customView.user_filters[0];

    if (userFilter.filter_type === "tags") {
      base.in("tags.id", userFilter.filter_settings.tags);
    }

    if (userFilter.filter_type === "channel_member") {
      base = supabase
        .from("slack_user_channels")
        .select("slack_users!inner(slack_id, name, tags(id, name), email)")
        .filter("channel", "eq", userFilter.filter_settings.channel)
        .neq("slack_users.is_bot", true)
        .neq("slack_users.is_deleted", true);
    }

    if (userFilter.filter_type === "date_joined") {
      if (platform === "slack") {
        base
          .gte("joined_formatted_ts", userFilter.filter_settings.start_date)
          .lte("joined_formatted_ts", userFilter.filter_settings.end_date);
      }
      if (platform === "discord") {
        base
          .gte("joined_at", userFilter.filter_settings.start_date)
          .lte("joined_at", userFilter.filter_settings.end_date);
      }
    }

    if (userFilter.filter_type === "roles") {
      base.in("discord_roles.discord_id", userFilter.filter_settings.roles);
    }

    if (userFilter.filter_type === "user_group") {
      base = supabase
        .from("slack_user_groups_users")
        .select("slack_users (slack_id, name, tags(id, name), email)")
        .filter(
          "slack_user_group",
          "eq",
          userFilter.filter_settings.user_group
        );
    }

    if (userFilter.filter_type === "timezone") {
      base.in("tz_label", userFilter.filter_settings.timezone);
    }

    // stack more filters here

    const response = await base;
    // paginate responses for larger communities
    const pageSize = 10000;
    let { data } = response;
    if (response.error) console.log("error in filterUsers", response.error);
    // if 10000 rows returned by the query, paginate
    else if (data.length >= pageSize) {
      let lastPageLength = data.length;
      let cursor = lastPageLength;
      while (lastPageLength >= pageSize) {
        // append a range modifier to the base query to get next page of results
        const nextPageQuery = base.range(cursor, cursor + pageSize);
        // eslint-disable-next-line
        let nextPageResult = await nextPageQuery;
        if (nextPageResult.error) {
          console.log("error in filterUsers", response.error);
          break;
        } else {
          data = data.concat(nextPageResult.data);
          lastPageLength = nextPageResult.data.length;
          cursor += lastPageLength;
        }
      }
    }
    if (userFilter.filter_type === "tags") {
      data = data.filter((row) => row.tags.length > 0);
    }
    if (userFilter.filter_type === "channel_member") {
      data = data.map((result) => result.slack_users);
    }
    // This is not necessary anymore because the roles is being filtered above
    // but, I write this message to the future if we want to refactor how filters work
    if (userFilter.filter_type === "roles") {
      data = data.filter((row) => row.discord_roles.length > 0);
    }
    if (userFilter.filter_type === "user_group") {
      data = data.map((result) => result.slack_users);
    }
    setFilteredUsers(data);
    return data;
  };

  const getView = async () => {
    const { data, error } = await supabase
      .from("user_views")
      .select(
        "name, user_filters(id, filter_type, filter_settings), description"
      )
      .match({ id: props.viewId });
    if (error) {
      console.log("error in getView", error);
      return null;
    }
    setView(data[0]);
    return data[0];
  };

  const getAnalyticsForUsers = async (users, start, end) => {
    const pageSize = 10000;
    let userIds;
    const params = {
      start_time: start,
      end_time: end,
    };
    let userActivity;
    let userActivityData = [];

    if (platform === "slack") {
      userIds = users.map((user) => user.slack_id);
      params.team_id = teamId;
    } else {
      userIds = users.map((user) => user.id);
      params.guild_id = teamId;
    }

    let cursor = 0;
    // since our active_users_view RPC can only return 10k rows, pass first 10k userIDs to RPC
    let userIDsBatch = userIds.slice(cursor, cursor + pageSize);

    while (userIDsBatch.length) {
      if (platform === "slack") {
        params.slack_ids = userIDsBatch;
        // eslint-disable-next-line
        userActivity = await supabase.rpc("active_users_view", params);
        if (userActivity.error) {
          console.error("error in getAnalyticsForUsers", userActivity.error);
          return null;
        }
        userActivityData = userActivityData.concat(
          userActivity.data.map((result) => {
            return {
              key: result.user_id,
              slack_id: result.user_id,
              name: result.user_name,
              user_name: result.user_name,
              image_32: result.image_32,
              email: result.email,
              tags: result.tags_json,
              is_admin: result.is_admin,
              tz_label: result.tz_label,
              parent_count: result.parent_count,
              thread_count: result.thread_count,
              reaction_count: result.reaction_count,
              total_count: result.total_count,
              total_messages_count: result.total_messages_count,
            };
          })
        );
      } else {
        params.member_ids = userIDsBatch;
        // eslint-disable-next-line
        userActivity = await supabase.rpc(
          "discord_active_members_view",
          params
        );
        if (userActivity.error) {
          console.error("error in getAnalyticsForUsers", userActivity.error);
          return null;
        }
        userActivityData = userActivityData.concat(
          userActivity.data.map((result) => {
            return {
              key: result.id,
              id: result.id,
              discord_id: result.discord_id,
              name: result.username,
              username: result.username,
              avatar: result.avatar,
              tags: result.tags,
              discord_roles: result.roles,
              parent_count: result.parent_count,
              thread_count: result.thread_count,
              reaction_count: result.reaction_count,
              total_count: result.total_count,
              total_messages_count: result.total_messages_count,
            };
          })
        );
      }
      // get next 10k userIDs to pass into RPC on next iteration
      cursor += pageSize;
      userIDsBatch = userIds.slice(cursor, cursor + pageSize);
    }

    /*
     Calculation Basic Metrics
     create and set 4 variables using above information
    */
    setTotalMembers(userActivityData.length);
    const sumMessages = (previousValue, currentValue) => {
      return previousValue + currentValue.total_messages_count;
    };
    const sumMessagesCount = userActivityData.reduce(sumMessages, 0);
    const sumReaction = (previousValue, currentValue) => {
      return previousValue + currentValue.reaction_count;
    };
    const sumReactionCount = userActivityData.reduce(sumReaction, 0);

    const sumActive = (previousValue, currentValue) => {
      if (
        currentValue.total_messages_count > 0 ||
        currentValue.reaction_count > 0
      )
        return previousValue + 1;
      return previousValue;
    };
    const sumActiveCount = userActivityData.reduce(sumActive, 0);
    setMessagesSent(sumMessagesCount);
    setActiveThisPeriod(sumActiveCount);
    setReactionAdded(sumReactionCount);
    setUserActivity(userActivityData);
    setFilteredByRole(userActivityData);
    return userActivityData;
  };

  const loadData = async (start, end) => {
    setLoading(true);
    const customView = await getView();
    const users = await filterUsers(customView);
    await getAnalyticsForUsers(users, start, end);
    setLoading(false);
  };

  const resetAll = () => {
    setFilteredByRole(filteredUsersActivity);
  };

  /**
   * Purpose: Delete a tab
   * Params
   */
  const deleteTab = async () => {
    const { data, error } = await supabase
      .from("user_views")
      .delete()
      .match({ id: props.viewId });
    if (error) {
      console.log("error", error);
      message.info("Error deleting. Please try again!");
    }
    if (data) {
      message.info("Custom view deleted!");
      window.analytics.track("Custom View deleted");
      props.loadViews();
    }
  };

  const activityColumns = [
    {
      title: "# convos started",
      dataIndex: "parent_count",
      key: "parent_count",
      sorter: (a, b) => {
        return a.parent_count - b.parent_count;
      },
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "# replies sent",
      dataIndex: "thread_count",
      key: "thread_count",
      sorter: (a, b) => {
        return a.thread_count - b.thread_count;
      },
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Total messages sent",
      dataIndex: "total_messages_count",
      key: "total_messages_count",
      sorter: (a, b) => {
        return a.total_messages_count - b.total_messages_count;
      },
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "# reactions added",
      dataIndex: "reaction_count",
      sorter: (a, b) => {
        return a.reaction_count - b.reaction_count;
      },
      sortDirections: ["descend", "ascend"],
    },
  ];
  useEffect(() => {
    loadData(props.startTime, props.endTime);
  }, [props.startTime, props.endTime]);

  // count number of messages
  let count = 0;
  if (filteredUsersActivity) {
    filteredUsersActivity.forEach((item) => {
      count += item.total_messages_count;
    });
  }

  const textDescription = view.description
    ? `Description: ${view.description}`
    : null;
  // concat with total members
  const viewDescription = textDescription;
  return (
    <div>
      {platform === "slack" ? (
        <UserTable
          startTime={props.startTime}
          endTime={props.endTime}
          teamId={teamId}
          loading={loading}
          loadPageData={loadData}
          allUsers={filteredUsersActivity}
          otherColumns={activityColumns}
          loadParentData={loadData}
          deleteTab={deleteTab}
          description={viewDescription}
          showLineChartAnalytics
          totalMembers={totalMembers}
          messagesSent={messagesSent}
          activeThisPeriod={activeThisPeriod}
          reactionAdded={reactionAdded}
        />
      ) : (
        <DiscordMembersTable
          members={filteredUsersActivity}
          membersFiltered={filteredUsersActivityByRole}
          memberReset={resetAll}
          memberUpdate={setFilteredByRole}
          loadPageData={loadData}
          deleteTab={deleteTab}
          startTime={props.startTime}
          endTime={props.endTime}
          description={viewDescription}
          isLoading={loading}
          showActivityColumns
          onGuildTagUpdate={props.onGuildTagUpdate}
          onMemberTagAdded={addMemberTagAllTabs}
          onMemberTagRemoved={removeMemberTagAllTabs}
          setIsBulkEditModalVisible={props.setIsBulkEditModalVisible}
          isBulkEditModalVisible={props.isBulkEditModalVisible}
          onBulkTag={props.onBulkTag}
          isCustomView
        />
      )}
    </div>
  );
};

export default CustomFilterTab;
